import {Question} from "../models/Question";
import {ControlType} from "../models/ControlType";
import {getEzAppToken} from "./Utilities";
import {QuestionPage} from "../models/QuestionPage";
import {QuestionSection} from "../models/QuestionSection";
import {UpdateQuestionRequest} from "../models/UpdateQuestionRequest";
import {
    CaseId,
    EzAppBaseUrl,
    QuestionAnswerMeta,
    QuestionAnswers,
    StoredMedicalQuestions,
    SuspectedConditions
} from "./Store";
import {get} from "svelte/store";
import {IUpdateDisclosureRequest} from "../models/IUpdateDisclosureRequest";
import {SuspectedCondition} from "../models/SuspectedCondition";
import {SetFatalError, SetIncidentalError} from "./ErrorService";
import {lockApplicationSelection} from "./NavigationService";
import {QuestionAnswer} from "../models/QuestionAnswer";
import {QuestionAnswerData} from "../models/QuestionAnswerData";


export function resetStoredMedicalQuestions() {
    let questionPages: QuestionPage[] = [];
    let questionAnswers: QuestionAnswer[] = [];
    let questionAnswerData = new QuestionAnswerData(0, -1);
    let suspectedConditions: SuspectedCondition[] = [];

    StoredMedicalQuestions.set(questionPages);
    QuestionAnswers.set(questionAnswers);
    QuestionAnswerMeta.set(questionAnswerData);
    SuspectedConditions.set(suspectedConditions);
}

export async function getBaseQuestionsFromService(policyNumber: string) {
    const baseUrl = get(EzAppBaseUrl);
    const url = baseUrl + 'Telelife/BaseQuestions/' + policyNumber;
    let questions: QuestionPage[] = [];
    lockApplicationSelection(true);

    await fetch (url, {
        method: 'GET',
        mode: 'cors',
        cache: 'no-cache',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': getEzAppToken()
        }
    })
        .then(response => response.json())
        .then(data => mapBaseQuestions(data))
        .then(mappedData => questions = mappedData)
        .catch(() => {
            SetFatalError();
        });
    return questions;
}

export async function updateQuestionAnswer(question: Question) {
    const baseUrl = get(EzAppBaseUrl);
    const url = baseUrl + 'Telelife/PostQuestionResponse';
    const caseId = get(CaseId);
    const request = new UpdateQuestionRequest(caseId, question.code, question.value);
    let returnQuestion = new Question();

    await fetch(url, {
        method: 'PATCH',
        mode: 'cors',
        cache: 'no-cache',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': getEzAppToken()
        },
        body: JSON.stringify(request)
    })
        .then(response => response.json())
        .then(data => mapUpdateQuestionResponse(data))
        .then(mappedData => returnQuestion = mappedData)
        .catch((error) => {
            console.log('Error from question answer: ' + error);
           SetIncidentalError();
        });

    return returnQuestion;
}

export async function removeQuestionAnswer(question: Question) {
    const baseUrl = get(EzAppBaseUrl);
    const url = baseUrl + 'Telelife/DeleteQuestionResponse';
    const caseId = get(CaseId);
    const request = new UpdateQuestionRequest(caseId, question.code, question.value);
    let returnQuestion = new Question();

    await fetch(url, {
        method: 'PATCH',
        mode: 'cors',
        cache: 'no-cache',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': getEzAppToken()
        },
        body: JSON.stringify(request)
    })
        .then(response => response.json())
        .then(data => mapRemoveQuestionResponse(data))
        .then(mappedData => returnQuestion = mappedData)
        .catch(() => {
            SetIncidentalError();
        });

    return returnQuestion;
}

export async function getDisclosureList() {
    const baseUrl = get(EzAppBaseUrl);
    const url = baseUrl + 'Telelife/GetDisclosuresList';
    let disclosures = [];
    await fetch(url, {
        method: 'GET',
        mode: 'cors',
        cache: 'no-cache',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': getEzAppToken()
        }
    })
        .then(response => response.json())
        .then(data => disclosures = data)
        .catch(() => {
            SetIncidentalError();
        });
    return disclosures;
}

export async function updateDisclosure(request: IUpdateDisclosureRequest) {
    const baseUrl = get(EzAppBaseUrl);
    const url = baseUrl + 'Telelife/UpdateDisclosure';
    request.caseId = get(CaseId);
    let disclosureResponse;
    await fetch(url, {
        method: 'POST',
        mode: 'cors',
        cache: 'no-cache',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': getEzAppToken()
        },
        body: JSON.stringify(request)
    })
        .then(response => response.json())
        .then(data => disclosureResponse = data)
        .catch(() => {
            SetIncidentalError();
        });
    return disclosureResponse;
}

export async function updateSpecifiedDisclosure(request: IUpdateDisclosureRequest) {
    const baseUrl = get(EzAppBaseUrl);
    const url = baseUrl + 'Telelife/UpdateSpecifiedDisclosure';
    request.caseId = get(CaseId);
    let disclosureResponse;
    await fetch(url, {
        method: 'POST',
        mode: 'cors',
        cache: 'no-cache',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': getEzAppToken()
        },
        body: JSON.stringify(request)
    })
        .then(response => response.json())
        .then(data => disclosureResponse = data)
        .catch(() => {
            SetIncidentalError()
        });
    return disclosureResponse;
}

export async function retrieveSuspectedConditions(applicationId: string)  {
    const baseUrl = get(EzAppBaseUrl);
    const url = baseUrl + 'Telelife/GetConditionsForCase/' + applicationId;
    let suspectedConditions: SuspectedCondition[] = [];
    await fetch(url, {
        method: 'GET',
        mode: 'cors',
        cache: 'no-cache',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': getEzAppToken()
        }
    })
        .then(response => response.json())
        .then(data => mapSuspectedConditions(data))
        .then(mappedSuspectedConditions => suspectedConditions = mappedSuspectedConditions)
        .catch(() => {
            SetIncidentalError();
        });

    return suspectedConditions;
}

function mapSuspectedConditions(response: any): SuspectedCondition[] {
    let conditions: SuspectedCondition[] = [];

    if (response && response.length > 0) {
        response.map(c => {
            const newCondition = new SuspectedCondition();
            newCondition.displayAlias = c.displayAlias;
            newCondition.description = c.description;
            newCondition.baseQuestionCode = c.baseQuestionCode;
            newCondition.condition = c.condition;
            conditions.push(newCondition);
        })
    }
    return conditions;
}

function mapUpdateQuestionResponse(response: any): Question {
    const question = new Question();
    const result = response;
    question.code = result.code;
    question.questionType = result.questionType;
    question.text = result.text;
    question.requiresAction = result.requiresAction;
    question.clarifyingAction = result.clarifyingAction;
    question.answerType = result.answerType;
    question.answerText = result.answerText;
    question.value = result.value;
    question.disclosureSource = result.disclosureSource;
    question.disclosureAlias = result.disclosureAlias;
    question.showSuspectedConditions = result.showSuspectedConditions;
    question.showPicklist = result.showPicklist;
    question.showFreeformTextBox = result.showFreeformTextBox;
    question.pickListSearchEnabled = result.pickListSearchEnabled;

    result.pickListItems.map(i => {
        question.pickListItems.push(i);
    })

    if (result.suspectedConditions) {
        result.suspectedConditions.map(i => {
            question.suspectedConditions.push(i);
        })
    }

    return question;
}

function mapRemoveQuestionResponse(response: any): Question {
    const question = new Question();
    question.code = response.code;
    question.answerText = 'Not Sure';
    question.value = '';
    question.text = response.questionText;
    question.suspectedConditions = [];
    question.pickListItems = [];
    question.pickListSearchEnabled = false;
    question.showFreeformTextBox = false;
    question.showPicklist = false;
    question.showSuspectedConditions = false;
    question.questionType = response.questionType;

    return question;
}

export function mapBaseQuestions(questions: any): QuestionPage[] {
    lockApplicationSelection(false);
    let questionPages: QuestionPage[] = [];

    const pages = questions.pages;
    pages.map(page => {
       const questionPage = new QuestionPage();
       questionPage.code = page.code;
       questionPage.headerText = page.headerText;

       page.sections.map(section => {
           const questionSection = new QuestionSection();
           questionSection.code = section.code;
           questionSection.isImplicit = section.isImplicit;

           section.questions.map(question => {
               const q = new Question();
               q.code = question.code;
               q.questionType = question.questionType;
               q.text = question.text;
               q.requiresAction = question.requiresAction;
               q.clarifyingAction = question.clarifyingAction;
               q.answerType = question.answerType;
               q.answerText = question.answerText;
               q.value = question.value;
               q.disclosureSource = question.disclosureSource;
               q.disclosureAlias = question.disclosureAlias;
               q.categoryCode = question.categoryCode;
               q.categoryDescription = question.categoryDescription;
               q.pickListSearchEnabled = question.pickListSearchEnabled;
               q.showPicklist = question.showPicklist;
               q.showSuspectedConditions = question.showSuspectedConditions;
               q.showFreeformTextBox = question.showFreeformTextBox;

               switch (q.questionType) {
                   case 'HEADING':
                       q.controlType = ControlType.Header;
                       break;
                   case 'YES_NO':
                       q.controlType = ControlType.YesNo;
                       break;
                   case 'ENTER_DETAILS':
                       q.controlType = ControlType.Text;
                       break;
               }

               question.pickListItems.map(i => {
                   q.pickListItems.push(i);
               })

               question.suspectedConditions.map(i => {
                   q.suspectedConditions.push(i);
               })


                questionPage.questions.push(q);
           });
       });
       questionPages.push(questionPage);
    });
    return questionPages;
}

export function replaceQuestionInCollection(question: Question) {
    const questionPages = get(StoredMedicalQuestions);

    questionPages.map((page) => {
        const foundQuestion = page.questions.find(q => q.code === question.code);
        if (foundQuestion) {
            foundQuestion.suspectedConditions = question.suspectedConditions;
            foundQuestion.showSuspectedConditions = question.showSuspectedConditions;
            foundQuestion.showPicklist = question.showPicklist;
            foundQuestion.showFreeformTextBox = question.showFreeformTextBox;
            foundQuestion.pickListSearchEnabled = question.pickListSearchEnabled;
            foundQuestion.pickListItems = question.pickListItems;
            StoredMedicalQuestions.set(questionPages);
        }
    })
}


